'use client'

import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { useTokens } from '@hooks/useTokens'

type MobilePriceType = {
  fetchPrices: () => Promise<void>
  tokenPrices: Record<
    string,
    | {
        price: number
      }
    | undefined
  >
  loading: boolean
}

/**
 * Prices now ride along with the token payload from /api/treasury/tokens, so
 * this is a view over `useTokens` rather than its own Pyth/Raydium fetch.
 */
const useTokenPricesHook = (): MobilePriceType => {
  const { prices, loading, refetch } = useTokens()

  const tokenPrices = useMemo(() => {
    const byMint: Record<string, { price: number } | undefined> = {}
    Object.entries(prices || {}).forEach(([mint, price]) => {
      if (typeof price === 'number') byMint[mint] = { price }
    })
    return byMint
  }, [prices])

  const fetchPrices = useCallback(async () => {
    await refetch()
  }, [refetch])

  return {
    fetchPrices,
    tokenPrices,
    loading,
  }
}

export type TokenPricesManager = ReturnType<typeof useTokenPricesHook>

const TokenPricesContext = createContext<TokenPricesManager | null>(null)

const { Provider } = TokenPricesContext

const TokenPricesProvider = ({ children }: { children: ReactNode }) => {
  return <Provider value={useTokenPricesHook()}>{children}</Provider>
}

const useTokenPrices = (): TokenPricesManager => {
  const context = useContext(TokenPricesContext)
  if (!context) {
    throw new Error(
      'useTokenPrices has to be used within <TokenPricesProvider>',
    )
  }
  return context
}
export { useTokenPrices, TokenPricesProvider }
